import { observable, action, computed, toJS } from 'mobx'
import { reportEntryLog } from '@xt/client/utils/log'
import { BindAll, Debounce, Bind } from 'lodash-decorators'
import { PagePagination } from '@xt/client/utils/infinite-loading/index'
import type {
  HomeworkListResponse,
  DairyListResponse,
  CollectListResponse,
  CommonStudyCardUIData,
  UserPanelUIType,
  CourseUIType,
  UserStatisticsResponse,
  FocusCountResponse,
  OtherProductResponse,
  UserCenterResponse
} from './types.d'
import { EnumApiMy } from '@xt/client/api/my'
import { EnumApiUser } from '@xt/client/api/user'
import { EnumApiProduct } from '@xt/client/api/product'
import { EnumMyStudyMenu, EnumMyCenterPrivacyConfig, EnumFollowStatus } from '@xt/client/enums/my'
import { EnumVisibleRange } from '@xt/client/enums/homework'
import { CollectTypeEnum } from '@xt/client/enums/moment'
import lodash from 'lodash'
import { $service } from '@xt/client/experimental/request/service'
import { getContentTypeText } from '@xt/client/utils/my'
import { EnumApiHomework } from '@xt/client/api/homework'
import { CodeFLAG } from '@xt/client/dataSource/my'

@BindAll()
export class CenterController {
  @observable userData: UserPanelUIType | null = null
  @observable progressCourse: Array<CourseUIType> = []
  @observable isFollow: boolean = false
  @observable hideCompMap: Map<EnumMyCenterPrivacyConfig, boolean> = new Map()

  pagePagination = new PagePagination<CommonStudyCardUIData>({
    url: EnumApiMy.CenterHomework,
    pageSize: 20,
    mapValues: data => {
      return {
        dataSource: this.transformHomework(data)
      }
    }
  })

  @computed
  get experienceBackground() {
    if (this.userData == null) return '#F6F6F0'

    const colors = ['#F6F6F0', '#F6F0EA', '#F6F0EA', '#F6F0EA', '#F6EFEC', '#F6EDEA', '#F5ECE9', '#F5EBEB', '#F5E9EA', '#F5E6E9', '#F5E1E5']

    return colors[this.userData.level] || '#F6F6F0'
  }

  @computed
  get filterProgressCourse() {
    return lodash.chunk(this.progressCourse, 2)
  }

  async onLike(item: CommonStudyCardUIData) {
    if (item.like === true) return

    this.onChangeLikeStatus(item, 'like')

    const [, success] = await $service({
      method: 'PUT',
      url: `/moment/api/moments/like/{id}`,
      data: {
        id: item.id
      }
    })

    if (success !== true) {
      this.onChangeLikeStatus(item, 'revert')
    }
  }

  onRemoveItem(id: string | number) {
    const res = this.pagePagination.dataSource.filter(item => item.id !== id)
    this.pagePagination.updateSource(res)
  }

  onChangeLikeStatus(item: CommonStudyCardUIData, action: 'like' | 'revert') {
    const res = this.pagePagination.dataSource.map(data => {
      if (data.id === item.id) {
        return {
          ...data,
          like: action === 'like' ? true : false,
          likeCount: action === 'like' ? data.likeCount + 1 : Math.max(0, data.likeCount - 1)
        }
      } else {
        return data
      }
    })
    this.pagePagination.updateSource(res)
  }

  transformHomework(list: HomeworkListResponse[]): CommonStudyCardUIData[] {
    return list.map(item => {
      const hasImage = Array.isArray(item.picList) && item.picList.length > 0

      return {
        type: 'homework',
        reviewed: item.reviewed,
        reviewerId: item.reviewerId,
        reviewerNickname: item.reviewerNickname,
        id: item.id,
        cover: hasImage ? item.picList![0].waterUrl : undefined,
        coverWidth: hasImage ? item.picList![0].originalWidth : 348,
        coverHeight: hasImage ? item.picList![0].originalHeight : 464,
        isPublic: item.visibleRange !== EnumVisibleRange.Privacy,
        tag: item.title || '',
        title: item.homeworkTitle,
        content: item.content,
        memberId: item.memberId,
        memberNickname: item.memberNickname,
        like: false,
        likeCount: 0,
        showLike: false,
        bizType: item.bizType,
        createTime: item.createTime,
        parsed: false
      }
    })
  }

  transformDairy(list: DairyListResponse[]): CommonStudyCardUIData[] {
    return list.map(item => {
      const hasImage = Array.isArray(item.pictures) && item.pictures.length > 0

      return {
        type: 'dairy',
        id: item.id,
        cover: hasImage ? item.pictures[0].waterUrl : undefined,
        coverWidth: hasImage ? item.pictures[0].originWidth : 348,
        coverHeight: hasImage ? item.pictures[0].originHeight : 464,
        isPublic: item.visibleRange === EnumVisibleRange.Public,
        jumpUrl: item.jumpUrl,
        tag: item.sourceDescription || '',
        title: item.title,
        content: item.content,
        memberId: item.memberId,
        memberNickname: item.memberNickname,
        like: item.like === true,
        likeCount: typeof item.likeCount === 'number' ? item.likeCount : 0,
        showLike: true,
        createTime: item.createTime,
        parsed: false
      }
    })
  }

  transformCollect(list: CollectListResponse[]): CommonStudyCardUIData[] {
    return list.map(item => {
      return {
        type: 'collect',
        // 唯一
        id: item.collectionId,
        cover: item.coverPictureUrl ? item.coverPictureUrl : undefined,
        coverWidth: item.originalWidth || 348,
        coverHeight: item.originalHeight || 464,
        isPublic: true,
        tag: '',
        title: item.titleOrContent,
        content: item.titleOrContent,
        memberId: item.memberId,
        memberNickname: item.memberNickname,
        like: false,
        likeCount: 0,
        showLike: false,
        createTime: item.createTime,
        parsed: false,
        collectType: item.collectType,
        jumpType: item.collectType === CollectTypeEnum.WORK ? 'show' : 'dairy',
        collectionId: item.collectionId,
        showId: item.showId,
        videoUrl: item.videoUrl
      }
    })
  }

  mapPagePagination(value: EnumMyStudyMenu) {
    switch (value) {
      case EnumMyStudyMenu.Homework:
        this.pagePagination.updateStatus({
          url: EnumApiMy.CenterHomework,
          mapValues: data => {
            return {
              dataSource: this.transformHomework(data)
            }
          }
        })
        break
      case EnumMyStudyMenu.Reward:
        this.pagePagination.updateStatus({
          url: EnumApiMy.CenterWork,
          mapValues: data => {
            return {
              dataSource: this.transformDairy(data)
            }
          }
        })
        break
      case EnumMyStudyMenu.Daily:
        this.pagePagination.updateStatus({
          url: EnumApiMy.CenterDairy,
          mapValues: data => {
            return {
              dataSource: this.transformDairy(data)
            }
          }
        })
        break
      case EnumMyStudyMenu.Collect:
        this.pagePagination.updateStatus({
          url: EnumApiMy.CenterCollect,
          mapValues: data => {
            return {
              dataSource: this.transformCollect(data)
            }
          }
        })
        break
      default:
        break
    }
  }

  handleChangeContentList(value: EnumMyStudyMenu) {
    this.mapPagePagination(value)
    this.pagePagination.reset()
  }

  // 获取他人用户信息
  async loadUserData(memberId: string, isSelfVisit: boolean) {
    const [, res] = await $service<UserStatisticsResponse>({
      url: EnumApiUser.UserStatistics,
      data: { id: memberId }
    })

    const [, res1] = await $service<UserCenterResponse>({
      url: EnumApiHomework.UserCenter,
      params: { memberId }
    })

    const [, res2] = await $service<FocusCountResponse>({
      url: EnumApiUser.FocusCount,
      params: { memberId }
    })

    if (!res || !res1 || !res2) return null

    const { nickname, userName, learnTimeCounts, learnTimeWeek, sequentialLearnDays, level } = res

    const { totalLikeCount } = res1

    const { focusCount, fansCount } = res2

    this.setUserData({
      nickname,
      userName,
      followCount: Math.max(focusCount, 0),
      followedCount: Math.max(fansCount, 0),
      likeCount: totalLikeCount ?? 0,
      learnTime: Math.round(((learnTimeCounts ?? 0) / 3600) * 10) / 10,
      weekLearnTime: Math.floor((learnTimeWeek ?? 0) / 60),
      continueDays: sequentialLearnDays ?? 0,
      level: level ?? 0
    })

    if (isSelfVisit) return true

    const [, res3] = await $service<EnumFollowStatus>({
      url: EnumApiUser.FocusStatus,
      params: { targetMemberId: memberId }
    })

    if (typeof res3 !== 'number') return null

    this.setIsFollow(res3 === EnumFollowStatus.Yes)

    return true
  }

  transformProgressCourse(data: Array<OtherProductResponse>): Array<CourseUIType> {
    return data.map(item => {
      return {
        id: item.id,
        bizId: item.bizId,
        cover: item.coverUrl,
        title: item.productName,
        tag: getContentTypeText(item.contentType)
      }
    })
  }

  // 获取正在学习的课程
  async fetchProgressCourse(memberId: string) {
    const [, res] = await $service<Array<OtherProductResponse>>({
      url: EnumApiProduct.othersProducts,
      params: { memberId }
    })

    if (lodash.isArray(res)) {
      const data = this.transformProgressCourse(res)
      this.setProgressCourse(data)
    }
  }

  transformPrivacySettings(mask: number) {
    return [
      {
        key: EnumMyCenterPrivacyConfig.Homework,
        value: !!(mask & CodeFLAG.Homework_Flag)
      },
      {
        key: EnumMyCenterPrivacyConfig.Reward,
        value: !!(mask & CodeFLAG.Reward_Flag)
      },
      {
        key: EnumMyCenterPrivacyConfig.Daily,
        value: !!(mask & CodeFLAG.Daily_Flag)
      },
      {
        key: EnumMyCenterPrivacyConfig.Collect,
        value: !!(mask & CodeFLAG.Collect_Flag)
      },
      {
        key: EnumMyCenterPrivacyConfig.Experice,
        value: !!(mask & CodeFLAG.Experience_Flag)
      },
      {
        key: EnumMyCenterPrivacyConfig.Certificate,
        value: !!(mask & CodeFLAG.Certificate_Flag)
      },
      {
        key: EnumMyCenterPrivacyConfig.Award,
        value: !!(mask & CodeFLAG.Award_Flag)
      },
      {
        key: EnumMyCenterPrivacyConfig.Learning,
        value: !!(mask & CodeFLAG.Learning_Flag)
      },
      {
        key: EnumMyCenterPrivacyConfig.StudyData,
        value: !!(mask & CodeFLAG.StudyData_Flag)
      }
    ]
  }

  // 获取隐私设置
  async fetchPrivacySettings(memberId: string) {
    const [, mask] = await $service<number>({
      method: 'GET',
      url: EnumApiUser.GetConfig,
      data: {
        id: memberId
      }
    })

    if (typeof mask !== 'number') return

    // value true 代表公开，false 代表私密
    const settings = this.transformPrivacySettings(mask)

    settings.forEach(item => {
      this.setHideCompMap({
        [item.key]: !item.value
      })
    })
  }

  updateFollowData(follow: boolean) {
    if (!this.userData) return

    const count = !this.userData.followedCount ? 0 : Math.max(this.userData.followedCount, 0)

    this.setUserData({
      ...toJS(this.userData),
      followedCount: follow ? count + 1 : Math.max(count - 1, 0)
    })

    this.setIsFollow(follow)
  }

  @Debounce(300, { leading: true, trailing: false })
  @Bind()
  async toggleFollow(memberId: string) {
    // 更新关注状态
    this.setIsFollow(!this.isFollow)

    const [, res] = await $service<boolean>({
      url: EnumApiUser.FocusUser,
      method: 'POST',
      data: {
        focusStatus: this.isFollow ? EnumFollowStatus.Yes : EnumFollowStatus.No,
        followedId: memberId
      },
      params: {
        focusStatus: this.isFollow ? EnumFollowStatus.Yes : EnumFollowStatus.No,
        followedId: memberId
      }
    })

    // 成功
    if (res === true) {
      this.updateFollowData(this.isFollow)
      return
    }

    // 回滚
    this.setIsFollow(!this.isFollow)
  }

  // 初始化页面数据
  async initPageData(memberId: string, isSelfVisit: boolean) {
    if (!memberId) throw new Error('缺少用户id')

    const res = await this.loadUserData(memberId, isSelfVisit)

    if (!res) throw new Error('网络错误')

    await this.fetchProgressCourse(memberId)

    await this.fetchPrivacySettings(memberId)

    return true
  }

  @action
  setHideCompMap(data: Partial<Record<EnumMyCenterPrivacyConfig, boolean>>) {
    Object.keys(data).forEach(key => {
      this.hideCompMap.set(key as EnumMyCenterPrivacyConfig, data[key])
    })
  }

  @action
  setProgressCourse(data: Array<CourseUIType>) {
    this.progressCourse = data
  }

  @action
  setIsFollow(follow: boolean) {
    this.isFollow = follow
  }

  @action
  setUserData(data: UserPanelUIType) {
    this.userData = {
      ...data
    }
  }

  @action
  reset() {
    this.userData = null
    this.progressCourse = []
    this.isFollow = false
    this.hideCompMap.clear()
  }
}
